import * as signalR from '@microsoft/signalr';

/**
 * Нормализует базовый URL (без завершающего слэша) и добавляет путь хаба.
 */
function getHubUrl(baseUrl) {
  const base = (baseUrl || '').replace(/\/+$/, '');
  return `${base}/hubs/chat`;
}

/**
 * Преобразует сообщение с сервера в формат UI.
 * direction === 1 → выравнивание слева, direction === -1 → справа.
 */
function mapMessage(msg) {
  const rawDir = msg.Direction ?? msg.direction;
  const direction = (rawDir === 1 || rawDir === '1') ? 1 : -1;
  const isFromOperator = direction === 1;
  return {
    id: msg.Id ?? msg.id ?? Date.now(),
    text: msg.Body ?? msg.body ?? msg.Text ?? msg.text ?? '',
    sender: isFromOperator ? 'operator' : 'user',
    direction,
    timestamp: formatTime(msg.CreatedOn ?? msg.createdOn ?? msg.CreatedAt ?? msg.createdAt),
    raw: msg
  };
}

function formatTime(value) {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Создаёт и возвращает объект для работы с чатом через SignalR.
 * @param {string} hubBaseUrl - Базовый URL API (из .env REACT_APP_CHAT_HUB_URL).
 * @param {string} tokenCode - Код токена канала (при встраивании виджета).
 * @param {string} code - Код диалога (из getOrCreateDialogCode).
 * @param {object} callbacks - { onReceiveMessage, onError, onConnectionStateChange }
 */
export function createChatConnection(hubBaseUrl, tokenCode, code, callbacks = {}) {
  const hubUrl = getHubUrl(hubBaseUrl);
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(hubUrl, {
      withCredentials: false
    })
    .withAutomaticReconnect()
    .build();

  const { onReceiveMessage, onError, onConnectionStateChange } = callbacks;

  connection.on('ReceiveMessage', (message) => {
    if (onReceiveMessage) onReceiveMessage(mapMessage(message));
  });

  connection.on('Error', (text) => {
    if (onError) onError(text);
  });

  if (onConnectionStateChange) {
    connection.onreconnecting(() => onConnectionStateChange('reconnecting'));
    connection.onreconnected(() => onConnectionStateChange('connected'));
    connection.onclose((err) => onConnectionStateChange('closed', err));
  }

  return {
    connection,

    async start() {
      await connection.start();
    },

    async join() {
      const result = await connection.invoke('Join', tokenCode, code);
      if (result && result.error) {
        throw new Error(result.error);
      }
      return result;
    },

    async sendMessage(text, url = '', pageName = '') {
      const result = await connection.invoke(
        'SendMessage',
        code,
        tokenCode,
        url || (typeof window !== 'undefined' ? window.location.href : ''),
        pageName || (typeof document !== 'undefined' ? document.title : ''),
        text
      );
      if (result && result.error) {
        throw new Error(result.error);
      }
      return result;
    },

    stop() {
      return connection.stop();
    }
  };
}

export { mapMessage, getHubUrl };
